import { useTranslation } from "react-i18next";
import { IDashboardStats } from "../../models/DashboardStats";
import DashboardTile from "./DashboardTile";

interface IComplianceTableProps {
  dashboardStats: IDashboardStats | undefined;
  span?: 1 | 2;
}

type DashboardStatsExtended = IDashboardStats & {
  vehiclesAwaitingCOD?: number;
  vehiclesAwaitingID?: number;
  vehiclesAwaitingPayment?: number;
  vehiclesAwaitingCompletion?: number;
  failedPayments?: number;
};

const rowConfig: { key: keyof DashboardStatsExtended; label: string; bgClass: string; textClass: string }[] = [
  { key: "vehiclesAwaitingCOD", label: "Vehicles Awaiting COD", bgClass: "bg-amber-50", textClass: "text-amber-700" },
  { key: "vehiclesAwaitingID", label: "Vehicles Awaiting ID", bgClass: "bg-blue-50", textClass: "text-blue-700" },
  { key: "vehiclesAwaitingPayment", label: "Vehicles Awaiting Payment", bgClass: "bg-emerald-50", textClass: "text-emerald-700" },
  { key: "vehiclesAwaitingCompletion", label: "Vehicles Awaiting Completion", bgClass: "bg-violet-50", textClass: "text-violet-700" },
  { key: "failedPayments", label: "Failed Payments", bgClass: "bg-red-50", textClass: "text-red-700" },
];

function formatValue(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return String(value);
}

export default function ComplianceTable({ dashboardStats, span = 2 }: IComplianceTableProps) {
  const { t } = useTranslation("dashboard");

  if (!dashboardStats) return null;

  const stats = dashboardStats as DashboardStatsExtended;

  return (
    <DashboardTile span={span}>
      <div className="p-3">
        <div className="mb-3 w-full">
          <h3 className="prose font-semibold text-gray-900">{t("Compliance")}</h3>
          <p className="text-sm text-gray-500 mt-0.5">{t("Vehicles and payments awaiting action")}</p>
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200">
                <th className="pb-2.5 pt-2.5 pl-4 pr-4 font-semibold text-gray-800">{t("Metric")}</th>
                <th className="pb-2.5 pt-2.5 pr-4 text-right font-semibold text-gray-800">{t("Value")}</th>
              </tr>
            </thead>
            <tbody>
              {rowConfig.map(({ key, label, bgClass, textClass }) => {
                const value = stats[key] as number | null | undefined;
                return (
                  <tr key={String(key)} className={`font-medium transition-colors ${bgClass}`}>
                    <td className={`py-2.5 pl-4 pr-4 ${textClass}`}>{t(label)}</td>
                    <td className={`py-2.5 pr-4 text-right font-bold ${textClass}`}>{formatValue(value)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardTile>
  );
}
